import { cn } from '@fshr/shared-react';
import Button from '../..//button';
import { IconChevronLightRight, IconChevronLightLeft } from '../../icon';
import { FC, PropsWithChildren, useCallback } from 'react';
import { CustomArrowsProps, CustomPaginationProps } from './overlay-carousel-component';

type CarouselButtonsProps = {
	buttonsType?: ('rounded' | 'arrow')[];
	pages: number[];
	currentPage: number;
	goToPage: (page: number) => void;
	buttonLabels: { previous: string; next: string };
	disableButtons?: boolean;
	// Custom components
	CustomPagination?: FC<PropsWithChildren<CustomPaginationProps>>;
	CustomArrows?: FC<PropsWithChildren<CustomArrowsProps>>;
};

const OverlayCarouselArrows = (props: {
	childIndex: number;
	minChildIndex: number;
	maxChildIndex: number;
	setChildIndex: (fn: (n: number) => number) => void;
	incrementAmount?: number;
	labels: {
		previous: string;
		next: string;
	};
}) => {
	const {
		childIndex,
		minChildIndex,
		maxChildIndex,
		setChildIndex,
		incrementAmount = 1,
		labels,
	} = props;

	const increment = useCallback((n: number) => n + incrementAmount, [incrementAmount]);
	const decrement = useCallback((n: number) => n - incrementAmount, [incrementAmount]);

	return (
		<div
			className={cn(
				'absolute inset-y-0 left-0 right-0 flex items-center justify-between pointer-events-none transition-opacity duration-300',
				'opacity-0 group-hover:opacity-100'
			)}
		>
			<div className={cn('relative pointer-events-auto', 'ml-2')}>
				{childIndex > minChildIndex && (
					<button
						type="button"
						onClick={e => {
							e.preventDefault();
							e.stopPropagation();
							e.nativeEvent.stopImmediatePropagation();
							setChildIndex(decrement);
						}}
						className="w-300 h-300 relative z-10 flex items-center justify-center bg-white hover:bg-button-on-image-primary-hover rounded-full shadow-md transition-all duration-300 hover:scale-110"
						aria-label={labels.previous}
					>
						<IconChevronLightLeft width={12} height={12} />
					</button>
				)}
			</div>

			<div className={cn('relative pointer-events-auto', 'mr-2')}>
				{childIndex < maxChildIndex && (
					<button
						type="button"
						onClick={e => {
							e.preventDefault();
							e.stopPropagation();
							e.nativeEvent.stopImmediatePropagation();
							setChildIndex(increment);
						}}
						className="w-300 h-300 relative z-10 flex items-center justify-center bg-white hover:bg-button-on-image-primary-hover rounded-full shadow-md transition-all duration-300 hover:scale-110"
						aria-label={labels.next}
					>
						<IconChevronLightRight width={12} height={12} />
					</button>
				)}
			</div>
		</div>
	);
};

export const CarouselButtonsComponent = (props: CarouselButtonsProps) => {
	const {
		buttonsType,
		pages,
		currentPage,
		goToPage,
		buttonLabels,
		disableButtons,
		CustomPagination,
		CustomArrows,
	} = props;

	if (disableButtons || pages.length <= 1) return null;

	// If custom components are provided, render them instead
	if (CustomPagination || CustomArrows) {
		return (
			<div className="custom-carousel-controls">
				{CustomArrows ? (
					<CustomArrows currentPage={currentPage} totalPages={pages.length} goToPage={goToPage} />
				) : null}
				{CustomPagination ? (
					<CustomPagination
						pages={pages}
						currentPage={currentPage}
						goToPage={goToPage}
						totalItems={pages.length}
					/>
				) : null}
			</div>
		);
	}

	// Fall back to default behavior if no custom components
	if (!buttonsType?.length) return null;

	return (
		<div
			onClick={e => {
				e.preventDefault();
				e.stopPropagation();
				e.nativeEvent.stopImmediatePropagation();
			}}
			onMouseDown={e => {
				e.preventDefault();
				e.stopPropagation();
				e.nativeEvent.stopImmediatePropagation();
			}}
		>
			{buttonsType.includes('arrow') ? (
				<OverlayCarouselArrows
					childIndex={currentPage}
					minChildIndex={0}
					maxChildIndex={pages.length - 1}
					setChildIndex={fn => {
						const newPage = fn(currentPage);
						const clampedPage = Math.max(0, Math.min(newPage, pages.length - 1));
						goToPage(clampedPage);
					}}
					incrementAmount={1}
					labels={buttonLabels}
				/>
			) : null}
			{buttonsType.includes('rounded') ? (
				<div className="flex items-center justify-center gap-2">
					{pages.map((_, index) => (
						<Button
							intent="unstyled"
							key={index}
							onClick={e => {
								e.preventDefault();
								e.stopPropagation();
								e.nativeEvent.stopImmediatePropagation();
								goToPage(index);
							}}
							className={cn([
								'cursor-pointer',
								'rounded-full',
								'transition-colors',
								{
									'bg-text-disabled': index !== currentPage,
									'bg-black': index === currentPage,
									'w-[7px] h-[7px]': index < 2,
									'w-[6px] h-[6px]': index === 2,
									'w-[5px] h-[5px]': index > 2,
								},
							])}
							aria-label={`Go to page ${index + 1}`}
						/>
					))}
				</div>
			) : null}
		</div>
	);
};
